import { IServiceWithBiomarkerInfo } from '../store/slices/services';
import { ISalesOrderProduct } from '../store/slices/salesOrderProducts';
import {
  IDiagnoseReferenceRanges,
  IDiagnoseResult,
  IDigitalHealthProfile,
} from '../components/UI/SelectedSalesOrder/DigitalHealthProfileTab';
import { IDocument, IDocumentInput } from '../store/slices/documents';
import lungImage from '../assets/images/lung-auscultation.png';
import heartImage from '../assets/images/heart-auscultation.png';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { ServiceTranslation } from '../store/slices/salesOrders';

export const getAge = (birthDate: string): number =>
  Math.floor((new Date().getTime() - new Date(birthDate).getTime()) / 3.15576e10);

export const getFileExtensionFromMimetype = (mimetype: string): string => {
  if (mimetype && mimetype.includes('/')) {
    return mimetype.split('/')[1];
  }
  return '';
};

interface IBiomarkerValuesMinAndMax {
  minValue: number | undefined;
  maxValue: number | undefined;
}

const calculateVAT = (amount: number) => {
  const total = parseFloat(amount.toFixed(2));
  const vat = (total * 0.19) / 1.19;
  return vat === 0 ? '0' : vat.toFixed(2);
};

const calculateSubtotal = (amount: number) => {
  const total = parseFloat(amount.toFixed(2));
  const vat = total / 1.19;
  return vat === 0 ? '0' : vat.toFixed(2);
};

export const getServiceBiomarker = (
  servicesWithBiomarkers: IServiceWithBiomarkerInfo[],
  biomarkerId: number,
  serviceId: number
) => {
  let tempoBiomarkerValuesMinAndMax: IBiomarkerValuesMinAndMax = {
    minValue: undefined,
    maxValue: undefined,
  };
  const tempoServicesWithBiomarkers = servicesWithBiomarkers?.filter(
    (serviceWithBiomarker) =>
      serviceWithBiomarker.biomarker_id == biomarkerId &&
      serviceWithBiomarker.service_id == serviceId
  );
  if (tempoServicesWithBiomarkers) {
    tempoBiomarkerValuesMinAndMax = {
      minValue: tempoServicesWithBiomarkers[0]?.value1,
      maxValue: tempoServicesWithBiomarkers[0]?.value2,
    };
    for (let sBiomarker of tempoServicesWithBiomarkers) {
      if (
        sBiomarker.value1 &&
        tempoBiomarkerValuesMinAndMax.minValue &&
        +sBiomarker.value1 < +tempoBiomarkerValuesMinAndMax?.minValue
      ) {
        tempoBiomarkerValuesMinAndMax.minValue = sBiomarker.value1;
      }
      if (
        sBiomarker.value2 &&
        tempoBiomarkerValuesMinAndMax.maxValue &&
        +sBiomarker.value1 > +tempoBiomarkerValuesMinAndMax?.maxValue
      ) {
        tempoBiomarkerValuesMinAndMax.maxValue = sBiomarker.value2;
      }
    }
    return tempoBiomarkerValuesMinAndMax;
  }
  return tempoBiomarkerValuesMinAndMax;
};

export const isBiomarkerValueValid = (
  refValue1: number | undefined,
  refValue2: number | undefined,
  value: number
): boolean => {
  if (refValue1 && +value < +refValue1) {
    return false;
  }
  if (refValue2 && +value > +refValue2) {
    return false;
  }
  return true;
};

export const getPrintableHtml = (innerHtml: string, styleSheet: CSSStyleSheet[]) => {
  return `
  <html>
    <head>
      <style>
      ${styleSheet
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((cssRule) => cssRule.cssText)
            .join('');
        } catch (e) {
          return '';
        }
      })
      .join('')}
      </style>
    </head>
    <body>
      ${innerHtml}
    </body>
  </html>
  `;
};

export const groupDocumentsByDescription = (documents: IDocument[]) => {
  return documents.reduce((groups, document) => {
    const { attachment_type_description } = document;
    const key = attachment_type_description || 'Unknown';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(document);
    return groups;
  }, {} as { [key: string]: IDocument[] });
};

export const groupDocumentsToUploadByDescription = (documents: IDocumentInput[]) => {
  return documents.reduce((groups, documentInput) => {
    const { attachment_type_description } = documentInput.document;
    const key = attachment_type_description || 'Unknown';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(documentInput.document);
    return groups;
  }, {} as { [key: string]: IDocument[] });
};

export const mergeDocumentGroupsByDescription = (
  groupedDocuments: { [key: string]: IDocument[] },
  groupedDocumentsToUpload: { [key: string]: IDocument[] }
) => {
  const mergedGroups = { ...groupedDocuments };
  Object.keys(groupedDocumentsToUpload).forEach((attachment_type_description) => {
    if (!mergedGroups[attachment_type_description]) {
      mergedGroups[attachment_type_description] = [];
    }
    mergedGroups[attachment_type_description] = [
      ...mergedGroups[attachment_type_description],
      ...groupedDocumentsToUpload[attachment_type_description],
    ];
  });

  return mergedGroups;
};
export const getTranslatedServiceName = (
  servicesTranslations: any[],
  serviceId: string | number,
  lang: string
): string => {
  if (!servicesTranslations || servicesTranslations.length === 0) return '';

  const normalizeLang = (l: string) => l.trim().toLowerCase().slice(0, 2);
  const normalizedLang = normalizeLang(lang);

  const serviceTranslation = servicesTranslations.find(
    (st) => st.service_id?.toString() === serviceId.toString()
  );

  if (!serviceTranslation) return '';

  const translation = serviceTranslation.translations.find(
    (t: any) => normalizeLang(t.language) === normalizedLang
  );

  return translation?.service_name || '';
};

export const deviceType = (): 'portrait' | 'landscape' | 'desktop' => {
  if (window.innerWidth < 961) return 'portrait';
  else if (window.innerWidth < 1440) return 'landscape';
  return 'desktop';
};

export const barCodeValidation = (
  scanValue: string,
  personIdSelected: number | undefined,
  isPcr: boolean | undefined,
  isAntigenAntibody: boolean | undefined
) => {
  const pcrRegex = /(^EC|^LU)\d{8,8}$/;
  const antigenRegex = /(^AT|^LA)\d{8,8}$/;

  return (
    !(scanValue.trim().length > 0 && personIdSelected !== undefined) ||
    (isPcr && !pcrRegex.test(scanValue.trim())) ||
    (isAntigenAntibody && !antigenRegex.test(scanValue.trim()))
  );
};

export const numberToSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let formattedTime = `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
  if (hours >= 1) {
    formattedTime = `${hours.toString().padStart(2, '0')}:${formattedTime}`;
  }
  return formattedTime;
};

export const isFileTypeAllowed = (fileType: string) => {
  return (
    fileType.startsWith('image/') || fileType === 'application/pdf' || fileType.startsWith('video/')
  );
};

export const getTextByLanguage = (
  body: { language: string; text: string }[],
  selectedLanguage?: string
) => {
  return (
    body.find((value) => value.language === (selectedLanguage ?? localStorage.getItem('lang')))
      ?.text ?? ''
  );
};

export const getListByLanguage = <T extends { language: string }>(
  body: T[],
  selectedLanguage?: string
) => {
  return body.filter(
    (value) => value.language === (selectedLanguage ?? localStorage.getItem('lang'))
  );
};

export const removeInlineStyles = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const cleanStyles = (element: Element): void => {
    element.removeAttribute('style');
    Array.from(element.children).forEach((child) => {
      if (child instanceof Element) {
        cleanStyles(child);
      }
    });
  };

  cleanStyles(tempDiv);

  return tempDiv.innerHTML;
};

export const removeHtmlTags = (input: string) => {
  return input.replace(/<[^>]*>/g, '');
};

export const isAutoGeneratedEmail = (email: string): boolean => {
  if (email && email.includes('noverify@ecocare.center')) return true;
  return false;
};

const K = 1024;
const SIZES = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

export const formatSize = (kilobytes: number, decimals = 2) => {
  if (kilobytes === 0) return '0 KB';
  const i = Math.floor(Math.log(kilobytes) / Math.log(K));
  return parseFloat((kilobytes / Math.pow(K, i)).toFixed(decimals)) + ' ' + SIZES[i];
};

export const formatTime = (timer: number) => {
  const getSeconds = `0${timer % 60}`.slice(-2);
  const minutes = `${Math.floor(timer / 60)}`;
  const getMinutes = `0${Number(minutes) % 60}`.slice(-2);
  const getHours = `0${Math.floor(timer / 3600)}`.slice(-2);

  return `${getHours} : ${getMinutes} : ${getSeconds}`;
};

export const getInitialsFromName = (name: string) => {
  return name
    .split(' ')
    .map(function (item) {
      return item[0];
    })
    .join('')
    .toUpperCase();
};

export interface IMappedSalesOrder {
  sales_order_id: number;
  sales_order_item_id?: number;
  sales_order_external_id: any;
  customer_person_id: any;
  datetime_create: Date;
  person_firstname: string;
  person_lastname: string;
  person_gender_id: any;
  person_birthdate: Date;
  person_email: string;
  person_mobile_phone: string;
  person_nationality_id: any;
  business_person_id: any;
  items_subtotal: number;
  paid_amount: number;
  vat: string | number;
  record_state: string;
  currency: string;
  payment_method_id: any;
  person_id_create: any;
  ta_id: any;
  transaction_id: any;
  payment_method?: string;
  paid?: string;
  unit_of_measure_id?: any;
  voucher_amount?: any;
  products: IMappedProductDetail[];
}

export interface IMappedProductDetail {
  sales_order_id?: number;
  sales_order_item_id?: number | string;
  product_id?: string;
  product_name?: string;
  start?: string;
  end_time?: string;
  quantity?: string;
  amount?: string;
  amount_paid?: string;
  price?: number | null;
  item_record_state: string;
}

interface Accumulator {
  [salesOrderId: string]: IMappedSalesOrder;
}

export const transformSalesOrders = (
  data: ISalesOrderProduct[],
  sortingColumn?: 'booking_time' | 'appointment_time'
): IMappedSalesOrder[] => {
  const grouped = data.reduce((acc: Accumulator, obj: ISalesOrderProduct) => {
    const {
      product_id,
      sales_order_item_id,
      product_name,
      start,
      end_time,
      quantity,
      amount,
      amount_paid,
      voucher_amount,
      vat,
      price,
      item_record_state,
      ...orderDetails
    } = obj;
    const key = obj.sales_order_id?.toString();
    if (!acc[key]) {
      acc[key] = {
        ...orderDetails,
        items_subtotal: 0,
        paid_amount: 0,
        vat: 0,
        products: [],
      };
    }
    acc[key].items_subtotal = parseFloat(
      calculateSubtotal(
        parseFloat(amount?.toString() ?? '0') - parseFloat(voucher_amount?.toString() ?? '0')
      )
    );
    acc[key].paid_amount = parseFloat(amount_paid?.toString() ?? '0');
    acc[key].vat = calculateVAT(
      parseFloat(amount?.toString() ?? '0') - parseFloat(voucher_amount?.toString() ?? '0')
    );
    acc[key].products.push({
      sales_order_id: acc[key].sales_order_id,
      sales_order_item_id: sales_order_item_id?.toString(),
      product_id: `${acc[key].sales_order_id}/${acc[key].sales_order_external_id
        }/${sales_order_item_id?.toString()}`,
      product_name,
      start: start?.toString(),
      end_time: end_time?.toString(),
      quantity,
      amount: `€ ${amount?.toString()}`,
      amount_paid: amount_paid?.toString(),
      price: price,
      item_record_state: item_record_state,
    });
    return acc;
  }, {});

  const _data = Object.values(grouped);

  if (sortingColumn === 'booking_time') {
    return _data.sort(
      (a, b) => new Date(b.datetime_create).getTime() - new Date(a.datetime_create).getTime()
    );
  }
  return _data.sort(
    (a, b) =>
      new Date(b?.products[0]?.start ?? '').getTime() -
      new Date(a?.products[0]?.start ?? '').getTime()
  );
};

export const salesOrderRecordStateToStatus = (
  recordState: string | undefined,
  missedConsultation: boolean
): {
  status: 'open' | 'closed' | 'cancelled' | 'missed' | '-';
  color: '#3DD47E' | '#858FB0' | '#E13838' | '#000';
} => {
  if (missedConsultation) {
    return {
      status: 'missed',
      color: '#E13838',
    };
  }
  switch (recordState) {
    case 'A':
      return {
        status: 'open',
        color: '#3DD47E',
      };
    case 'R':
      return {
        status: 'closed',
        color: '#858FB0',
      };
    case 'C':
      return {
        status: 'cancelled',
        color: '#E13838',
      };
    default:
      return {
        status: '-',
        color: '#000',
      };
  }
};

export const getNumericDHPRanges = (data: IDiagnoseReferenceRanges[]) => {
  const tmp: IDiagnoseReferenceRanges[] = [];
  data.forEach((item) => {
    tmp.push({
      ...item,
      property_range: JSON.parse(item.property_range),
    });
  });
  return tmp;
};

export const getBiomarkerRangeColorOrder = (
  biomarker: IDiagnoseResult,
  dhp: IDigitalHealthProfile
): number => {
  const findRange =
    dhp.diagnose_reference_ranges?.filter(
      (product) => product.property_id === biomarker.property_id
    ) ?? [];
  const value: number = parseFloat(biomarker.property_value ?? '0');

  let range_code: number = 0;

  for (const range of findRange) {
    const numeric: number[] = JSON.parse(range.property_range);
    if (value >= numeric[0] && value <= numeric[1]) {
      range_code = range.range_color_order;
      break;
    }
  }
  return range_code;
};

export const biomarkerRangeInPercentage = (
  biomarker: IDiagnoseResult,
  dhp: IDigitalHealthProfile,
  order: number
) => {
  const findRange = dhp.diagnose_reference_ranges?.filter(
    (product) =>
      product.property_id === biomarker.property_id && product.range_color_order === order
  );
  const rangeValues = findRange
    ? findRange[0]?.property_range?.match(/[\d.]+/g)?.map(Number) ?? [0, 1]
    : [0, 1];
  const biomarkerValue = parseFloat(biomarker.property_value);
  const percentage = ((biomarkerValue - rangeValues[0]) / (rangeValues[1] - rangeValues[0])) * 100;
  return percentage;
};

export const getBiomarkerLevelImage = (level: string) => {
  switch (level) {
    case 'Very Low':
      return 'biomarker-very-low';
    case 'Low':
      return 'biomarker-low';
    case 'Underweight':
    case 'Low-blue':
      return 'biomarker-low-blue';
    case 'Normal weight':
    case 'Normal':
    case 'Negative':
      return 'biomarker-normal';
    case 'Overweight':
    case 'High':
      return 'biomarker-high';
    case 'Obesity':
    case 'Very High':
      return 'biomarker-very-high';
    case 'Positive':
      return 'biomarker-positive';
    case 'Optimal':
      return 'optimal-range';
    case 'High Normal':
      return 'high-normal-range';
    case 'Grade 1':
      return 'grade-1-range';
    case 'Grade 2':
      return 'grade-2-range';
    case 'Grade 3':
      return 'grade-3-range';
    case 'Critical':
      return 'critical-range';
    case 'Pre-diabetic':
      return 'biomarker-high';
    case 'Diabetic':
      return 'biomarker-very-high';
    default:
      return 'biomarker-default';
  }
};

export function getTimeSlot(hour: number = new Date().getHours()) {
  if (hour >= 5 && hour < 12) {
    return 'Morning';
  } else if (hour >= 12 && hour < 17) {
    return 'Afternoon';
  } else if (hour >= 17 && hour < 21) {
    return 'Evening';
  } else {
    return 'Night';
  }
}

export const examinationFileType = (examination: any) => {
  if (examination?.attachments?.[0].mime_type.startsWith('image/')) return 'image';
  if (examination?.attachments?.[0].mime_type.startsWith('audio/')) return 'audio';
};

export const examinationContentImg = (examination: any) => {
  if (['Cough Examination', 'Lung Auscultation'].includes(examination.examination_name))
    return lungImage;
  return heartImage;
};

export const getAccountNumberFromEbaasPersonAccounts = (
  ebaasPersonAcconutResponse: AxiosResponse
): string | undefined => {
  return ebaasPersonAcconutResponse.data &&
    Array.isArray(ebaasPersonAcconutResponse.data) &&
    ebaasPersonAcconutResponse.data.length > 0 &&
    ebaasPersonAcconutResponse.data[0]?.account_number
    ? ebaasPersonAcconutResponse.data[0]?.account_number
    : undefined;
};

const PING_URL = 'https://www.google.com/generate_204'; // Lightweight & fast

export function useConnectionStatus(pingInterval: number = 10000) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isConnectionPoor, setIsConnectionPoor] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkConnection = async () => {
      try {
        const measurements = await Promise.allSettled(
          Array.from({ length: 3 }, async () => {
            const start = Date.now();
            const controller = new AbortController();
            setTimeout(() => controller.abort(), 5000);
            await fetch(PING_URL, {
              method: 'HEAD',
              signal: controller.signal,
              cache: 'no-store',
              mode: 'no-cors',
            });
            return Date.now() - start;
          })
        );

        const durations = measurements.map((r) => (r.status === 'fulfilled' ? r.value : 10000));
        const avgDuration = durations.reduce((a, b) => a + b, 0) / 3;
        const failedRequests = durations.filter((d) => d > 8000).length;

        setIsConnectionPoor(avgDuration > 5000 || failedRequests >= 2);
      } catch {
        setIsConnectionPoor(true);
      }
      timeoutId = setTimeout(checkConnection, pingInterval);
    };

    const initialTimeout = setTimeout(checkConnection, 2000);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialTimeout);
    };
  }, [pingInterval]);

  return { isOffline, isConnectionPoor };
}

interface Time {
  hh: string;
  mm: string;
}

export function convertMesurmentPlanJSON(data: any) {
  const selectedDays: string[] = [
    ...new Set(
      (data as Array<{ day_of_execution?: string }>)
        .map((n) => n.day_of_execution ?? "")
        .filter((day): day is string => typeof day === 'string' && day.trim() !== "")
        .map((day) => day.toLowerCase())
    ),
  ];
  const reminders = data.some((n: any) => n.reminder);

  const morningNotification = data.find((n: any) => n.is_morning);
  const morningTime: Time = morningNotification
    ? parseTimeString(morningNotification.time_of_execution)
    : { hh: "--", mm: "--" };

  // Find bedtime (from first bedtime notification)
  const bedtimeNotification = data.find((n: any) => n.is_bedtime);
  const bedtimeTime: Time = bedtimeNotification
    ? parseTimeString(bedtimeNotification.time_of_execution)
    : { hh: "--", mm: "--" };

  return {
    selectedDays,
    reminders,
    morningTime,
    bedtimeTime
  };
}

function parseTimeString(timeString: string): Time {
  // Parse "03:04:00+00" or "03:04:00-05" or "03:04:00+02:00" format and convert to user's local timezone
  // Match timezone pattern: [+-] followed by 1-2 digits, optionally followed by : and 2 digits
  const timezoneMatch = timeString.match(/([+-])(\d{1,2})(?::(\d{2}))?$/);

  if (timezoneMatch) {
    // Extract time part (everything before the timezone)
    const timePart = timeString.substring(0, timeString.indexOf(timezoneMatch[0])).trim();
    const [h, m] = timePart.split(':').map(Number);

    // Parse timezone offset
    const sign = timezoneMatch[1] === '+' ? 1 : -1;
    const offsetHours = parseInt(timezoneMatch[2], 10);
    const offsetMinutes = parseInt(timezoneMatch[3] || '0', 10);
    const serverOffsetTotalMinutes = sign * (offsetHours * 60 + offsetMinutes);

    // Create a moment object representing the server time in its timezone
    // Then convert to user's local timezone
    const serverTime = moment.utc(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, 'HH:mm:ss')
      .utcOffset(serverOffsetTotalMinutes);

    // Convert to user's local timezone
    const localTime = serverTime.local();

    return {
      hh: localTime.format('HH'),
      mm: localTime.format('mm')
    };
  }

  // If no timezone found, parse and return as is
  const timePart = timeString.split(/[+-]/)[0].trim();
  const [hours, minutes] = timePart.split(':');

  return {
    hh: hours.padStart(2, '0'),
    mm: minutes.padStart(2, '0')
  };
}

export const getCurrentTimeWithMicroseconds = () => {
  const now = moment();
  const ms = now.format("SSS");
  const micro = ms + Math.floor(Math.random() * 1000).toString().padStart(3, "0");
  return now.format(`YYYY-MM-DDTHH:mm:ss.${micro}Z`);
}

export const buildTranslatedServicesMap = (
  servicesTranslations: ServiceTranslation[] | undefined,
  lang: string
): Map<string, string> => {
  const map = new Map<string, string>();
  if (!servicesTranslations) return map;

  for (const t of servicesTranslations) {
    const match = t.translations.find(
      (tr) => tr.language.trim().toLowerCase() === lang.trim().toLowerCase()
    );
    if (match) {
      map.set(t.service_id.toString(), match.service_name);
    }
  }
  return map;
};

export const getTranslatedServicesName = (
  serviceId: string | number | undefined,
  translatedServicesMap: Map<string, string>,
  fallbackName?: string
): string => {
  if (!serviceId) return fallbackName ?? '';
  const name = translatedServicesMap.get(serviceId.toString());
  return name ?? fallbackName ?? '';
};
